import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import emrApi, { IDataUpsertEmr, IParamsGetEmr, IParamsGetGroup, IParamsGetGroupNext } from '@/apis/emr/api';
import { IRequesterResponse } from '@/utils/requester';
import utils from '@/utils';
import useGlobalConfigs from '@/stores/globalConfigs';
import dayjs from 'dayjs';

interface IOptionsUseUpsert {
  onError?: ((error: Error, variables: IDataUpsertEmr, context: unknown) => unknown) | undefined;
  onSuccess?: ((data: IRequesterResponse<any>, variables: IDataUpsertEmr, context: unknown) => unknown) | undefined;
}

const emrService = {
  useUpsert: (options?: IOptionsUseUpsert) => {
    return useMutation({
      mutationFn: (data: IDataUpsertEmr) => emrApi.upsert(data),
      ...options,
    });
  },

  useGetGroup: (params: IParamsGetGroup) => {
    const allEmrTimelineString = useGlobalConfigs((s) => s.phrConfigs?.viewTimeline)?.join(',');
    const getEmrGroups = useGlobalConfigs((s) => s.getEmrGroups);
    const groupsString = getEmrGroups().join(',');

    params.type = params.type || allEmrTimelineString;
    params.group = params.group || groupsString;
    params.end = dayjs().toISOString();

    const { uid, start, end, tag, type, group } = params;
    const query = useQuery({
      queryKey: ['emrGroups', uid, start, type, tag, group],
      queryFn: () => {
        return emrApi.getGroup({ uid, start, end, type, tag, group });
      },
    });
    return query;
  },

  useGetGroupNext: (params: IParamsGetGroupNext) => {
    const allEmrTimelineString = useGlobalConfigs((s) => s.phrConfigs?.viewTimeline)?.join(',');
    const getEmrGroups = useGlobalConfigs((s) => s.getEmrGroups);
    const groupsString = getEmrGroups().join(',');

    params.type = params.type || allEmrTimelineString;
    params.group = params.group || groupsString;

    const { uid, start, type, tag, group } = params;

    return useQuery({
      queryKey: ['emrGroupNext', uid, type, start, tag, group],
      queryFn: () => emrApi.getGroupNext(params),
    });
  },

  useGetEmr: (params: IParamsGetEmr) => {
    const query = useQuery({
      queryKey: ['getEmr', ...Object.values(params)],
      queryFn: () => emrApi.getEmr(params),
    });
    return query;
  },

  useGetGroupInfinite: (params: IParamsGetGroup, queryFnMiddleware?: (res: IRequesterResponse) => void) => {
    const initialPageParam = {
      end: dayjs().endOf('D').toISOString(),
      start: dayjs().add(-10, 'd').startOf('d').toISOString(),
    };
    const phrConfigs = useGlobalConfigs((s) => s.phrConfigs);
    const getEmrGroups = useGlobalConfigs((s) => s.getEmrGroups);
    const groupsString = getEmrGroups().join(',');

    params.type = params.type || phrConfigs?.viewTimeline?.join(',');
    params.group = params.group || groupsString;
    const { uid, tag, type, group } = params;
    const query = useInfiniteQuery({
      gcTime: 0,
      staleTime: 0,
      queryKey: ['useGetGroupInfinite', uid, tag, type, group],
      queryFn: async ({ pageParam }) => {
        const res = await emrApi.getGroupAndNext({
          uid,
          type,
          tag,
          start: pageParam.start,
          end: pageParam.end,
          group,
        });
        queryFnMiddleware?.(res);
        return res;
      },
      getNextPageParam: (lastResponse) => {
        return lastResponse?.meta;
      },
      initialPageParam,
    });
    return query;
  },

  usePreGetGroupEmr: (params: IParamsGetGroup) => {
    const queryClient = useQueryClient();
    const { uid, tag, type, group, start, end } = params;
    return queryClient.prefetchQuery({
      queryKey: ['usePreGetGroupEmr', uid, tag, type, group, start, end],
      queryFn: (a) => {
        console.log(a);
        debugger;
        return emrApi.getGroupAndNext(params);
      },
    });
  },

  useGetEmrInfinite: (params: IParamsGetEmr) => {
    const query = useInfiniteQuery({
      queryKey: [
        'useGetEmrInfinite',
        params.uid,
        params.start,
        params.end,
        params.type,
        params.filter,
        params.limit,
        params.offset,
      ],
      queryFn: ({ pageParam }) => emrApi.getEmr({ ...params, offset: pageParam }),
      getNextPageParam: (lastPage) => utils.parseQueryParams(lastPage?.meta?.next).offset,
      initialPageParam: 0,
    });
    return query;
  },

  useCountHr: (uid: string) => {
    const query = useQuery({
      queryKey: [uid],
      queryFn: () => emrApi.countHr(uid),
    });
    return query;
  },
};

export default emrService;
